import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconArrowLeft, IconCheck, IconGlobe, IconPlus, IconRefresh, IconTrash, IconX } from '../components/icons';
import { CopyButton, Empty, Field, Modal, Skeleton, useAsync, useToast } from '../components/ui';
import { api, Domain } from '../lib/api';
import { ago } from '../lib/format';
import { canWrite, useSession } from '../lib/session';
import { t } from '../lib/i18n';

const KIND_LABEL = { spf: 'SPF', dkim: 'DKIM', dmarc: 'DMARC' } as const;

export function DnsRecordsTable({ domain }: { domain: Domain }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>{t('Запись')}</th>
            <th>{t('Тип')}</th>
            <th>{t('Имя (host)')}</th>
            <th>{t('Значение')}</th>
            <th>{t('Статус')}</th>
          </tr>
        </thead>
        <tbody>
          {domain.records.map((r) => {
            const check = domain.checks[r.kind];
            return (
              <tr key={r.kind}>
                <td>
                  <strong>{KIND_LABEL[r.kind]}</strong>
                  <div className="small muted">{r.required ? t('обязательно') : t('рекомендуется')}</div>
                </td>
                <td><code>{r.type}</code></td>
                <td>
                  <div className="row">
                    <code>{r.host}</code>
                    <CopyButton small value={r.host} label="" track="domain.dns_copy" />
                  </div>
                </td>
                <td style={{ maxWidth: 340 }}>
                  <div className="row">
                    <code className="truncate" title={r.value} style={{ maxWidth: 260 }}>{r.value}</code>
                    <CopyButton small value={r.value} label="" track="domain.dns_copy" />
                  </div>
                  <div className="small muted" style={{ marginTop: 4 }}>{r.purpose}</div>
                </td>
                <td>
                  {!check ? (
                    <span className="pill pending">{t('не проверено')}</span>
                  ) : check.ok ? (
                    <span className="pill ok"><IconCheck size={12} /> {t('найдено')}</span>
                  ) : (
                    <span className={`pill ${r.required ? 'bad' : 'warn'}`} title={check.found.join('\n') || t('Записей не найдено')}>
                      <IconX size={12} /> {check.found.length ? t('не совпадает') : t('нет записи')}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DomainsPage() {
  const { me } = useSession();
  const { data, loading } = useAsync(() => api.get<Domain[]>('/domains'));
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const nav = useNavigate();

  async function add() {
    setError('');
    try {
      const d = await api.post<Domain>('/domains', { name });
      setAdding(false);
      nav(`/domains/${d.id}`);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Домены')}</h1>
          <p>{t('Домены, от имени которых вы отправляете письма. Подтверждённый домен подписывает каждое письмо DKIM.')}</p>
        </div>
        {canWrite(me) && (
          <button className="btn btn-primary" onClick={() => setAdding(true)} data-track="domain.add_click"><IconPlus size={16} /> {t('Добавить домен')}</button>
        )}
      </div>
      <div className="card">
        {loading ? (
          <div className="card-pad stack"><Skeleton /><Skeleton /></div>
        ) : !data?.length ? (
          <Empty icon={<IconGlobe size={36} />} title={t('Пока ни одного домена')} action={<button className="btn btn-primary" onClick={() => setAdding(true)}>{t('Добавить домен')}</button>}>
            {t('Добавьте домен, чтобы письма приходили от вашего имени и проходили SPF и DKIM.')}
          </Empty>
        ) : (
          <table className="table">
            <thead>
              <tr><th>{t('Домен')}</th><th>{t('Статус')}</th><th>SPF</th><th>DKIM</th><th>DMARC</th><th>{t('Проверен')}</th></tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.id} className="click" onClick={() => nav(`/domains/${d.id}`)}>
                  <td><strong>{d.name}</strong></td>
                  <td><span className={`pill ${d.status}`}>{d.status === 'verified' ? t('Подтверждён') : t('Ждём DNS')}</span></td>
                  {(['spf', 'dkim', 'dmarc'] as const).map((k) => (
                    <td key={k}>{d.checks[k]?.ok ? <IconCheck size={16} color="var(--kelp)" /> : <span className="muted">—</span>}</td>
                  ))}
                  <td className="muted">{ago(d.last_checked_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {adding && (
        <Modal
          title={t('Новый домен')}
          onClose={() => setAdding(false)}
          footer={
            <>
              <button className="btn" onClick={() => setAdding(false)}>{t('Отмена')}</button>
              <button className="btn btn-primary" onClick={add} disabled={!name}>{t('Добавить')}</button>
            </>
          }
        >
          <Field label={t('Домен')} hint={t('Лучше поддомен, например mail.example.com')} error={error}>
            <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name && add()} placeholder="mail.example.com" />
          </Field>
        </Modal>
      )}
    </>
  );
}

export function DomainDetailPage() {
  const { id = '' } = useParams();
  const { me } = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const { data: domain, loading, setData } = useAsync(() => api.get<Domain>(`/domains/${id}`), [id]);
  const [busy, setBusy] = useState(false);

  async function verify() {
    setBusy(true);
    try {
      const d = await api.post<Domain>(`/domains/${id}/verify`);
      setData(d);
      toast(d.status === 'verified' ? t('Домен подтверждён 🌊') : t('Записи пока не найдены'), d.status === 'verified' ? 'ok' : 'bad');
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (!domain || !confirm(t('Удалить домен {name}? Отправка с него сразу прекратится.', { name: domain.name }))) return;
    await api.del(`/domains/${id}`);
    toast(t('Домен удалён'));
    nav('/domains');
  }

  if (loading || !domain) return <div className="stack"><Skeleton h={32} w={300} /><Skeleton h={200} /></div>;
  return (
    <>
      <Link to="/domains" className="small row" style={{ marginBottom: 12 }}><IconArrowLeft size={14} /> {t('Все домены')}</Link>
      <div className="page-head">
        <div className="grow">
          <div className="row">
            <h1>{domain.name}</h1>
            <span className={`pill ${domain.status}`}>{domain.status === 'verified' ? t('Подтверждён') : t('Ждём DNS')}</span>
          </div>
          <p>{t('Последняя проверка: {when}', { when: ago(domain.last_checked_at) })}{domain.verified_at && <> · {t('подтверждён {when}', { when: ago(domain.verified_at) })}</>}</p>
        </div>
        {canWrite(me) && (
          <>
            <button className="btn btn-primary" onClick={verify} disabled={busy} data-track="domain.verify_click"><IconRefresh size={16} /> {busy ? t('Проверяем…') : t('Проверить DNS')}</button>
            <button className="btn btn-danger" onClick={remove}><IconTrash size={16} /></button>
          </>
        )}
      </div>
      <div className="card">
        <div className="card-head"><h3>DNS-записи</h3><span className="small muted">{t('Добавьте их у вашего DNS-провайдера')}</span></div>
        <DnsRecordsTable domain={domain} />
      </div>
      <div className="callout" style={{ marginTop: 16 }}>
        {t('Если у домена уже есть SPF-запись, не создавайте вторую — добавьте')} <code>include:amazonses.com</code> {t('в существующую. Две записи SPF делают проверку недействительной.')}
      </div>
    </>
  );
}
